import { useState, useEffect } from 'react';

const STORAGE_KEY = 'pwa-banner-shown';

function detect() {
  const ua = navigator.userAgent;
  const isIos = /iphone|ipad|ipod/i.test(ua);
  const isSafari = /safari/i.test(ua) && !/chrome/i.test(ua);
  const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
  return { isIos, isSafari, isStandalone };
}

export default function InstallPWA() {
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [mode, setMode] = useState(null); // 'android' | 'ios' | null

  useEffect(() => {
    if (localStorage.getItem(STORAGE_KEY)) return;

    const { isIos, isSafari, isStandalone } = detect();

    if (isStandalone) return;

    if (isIos && isSafari) {
      setMode('ios');
      return;
    }

    const handler = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setMode('android');
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, 'true');
    setMode(null);
  };

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') dismiss();
    setDeferredPrompt(null);
  };

  if (!mode) return null;

  if (mode === 'android') {
    return (
      <div className="fixed bottom-4 left-4 right-4 bg-white rounded-xl shadow-lg border border-purple-100 p-4 flex items-center gap-3 z-50">
        <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
          <span className="text-white text-lg font-bold">A</span>
        </div>
        <div className="flex-1">
          <p className="text-sm font-semibold text-gray-800">Instala AdamSched</p>
          <p className="text-xs text-gray-500">Accede más rápido desde tu móvil</p>
        </div>
        <div className="flex gap-2">
          <button onClick={dismiss} className="text-xs text-gray-400 px-2 py-1">
            No
          </button>
          <button
            onClick={handleInstall}
            className="text-xs bg-purple-600 text-white px-3 py-1 rounded-lg font-medium"
          >
            Instalar
          </button>
        </div>
      </div>
    );
  }

  // iOS Safari
  return (
    <div className="fixed bottom-4 left-4 right-4 bg-white rounded-xl shadow-lg border border-purple-100 p-4 z-50">
      <button
        onClick={dismiss}
        className="absolute top-2 right-2 text-gray-400 text-sm leading-none p-1"
        aria-label="Cerrar"
      >
        ✕
      </button>

      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 bg-purple-600 rounded-xl flex items-center justify-center flex-shrink-0">
          <span className="text-white font-bold">A</span>
        </div>
        <div>
          <p className="text-sm font-semibold text-gray-800">Instala AdamSched</p>
          <p className="text-xs text-gray-500">Accede más rápido desde tu móvil</p>
        </div>
      </div>

      <div className="space-y-2">
        <div className="flex items-center gap-2 text-xs text-gray-600">
          <span className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold flex-shrink-0">
            1
          </span>
          <span>
            Pulsa el botón
            <span className="inline-flex items-center gap-0.5 mx-1 text-blue-500">
              compartir
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8" />
                <polyline points="16 6 12 2 8 6" />
                <line x1="12" y1="2" x2="12" y2="15" />
              </svg>
            </span>
            de Safari
          </span>
        </div>

        <div className="flex items-center gap-2 text-xs text-gray-600">
          <span className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold flex-shrink-0">
            2
          </span>
          <span>Selecciona <strong>"Añadir a pantalla de inicio"</strong></span>
        </div>

        <div className="flex items-center gap-2 text-xs text-gray-600">
          <span className="w-5 h-5 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-bold flex-shrink-0">
            3
          </span>
          <span>Pulsa <strong>"Añadir"</strong> y listo</span>
        </div>
      </div>

      {/* Flecha apuntando hacia el botón de compartir de Safari */}
      <div className="absolute -bottom-2 left-1/2 -translate-x-1/2">
        <div className="w-4 h-4 bg-white border-r border-b border-purple-100 rotate-45" />
      </div>
    </div>
  );
}
